/** Invite people to a project, session or document through the same dropdown. */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import type { ProjectDoc, WorldDoc } from '@mod/core/model'
import type { InviteInclude, InviteLevel } from '@mod/core/sharing'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  catalogOf,
  type ProjectCatalog,
  resolveSubject,
} from '../../shell/window/resolveSubject'
import { useTabRepoBoot } from '../world/repo/TabRepoProvider'
import { useDocument } from '../world/repo/useDocument'
import { WORLD_KEY } from '../world/world'
import { useSharingAccount } from './accountContext'
import {
  type DesktopInvite,
  desktopInviteFor,
  desktopMembersFor,
} from './desktopShare'
import {
  type EmbedPlan,
  type EmbedSubject,
  planDocumentShare,
} from './embedPlan'
import { retainedDocumentIds, uniqueActiveInvites } from './inviteEntries'
import { MemberList, MemberStack, type Removal } from './MemberStack'
import type { MemberRow } from './membership'
import { useTargetMembership } from './useTargetMembership'

const LEVELS: Array<{ level: InviteLevel; label: string }> = [
  { level: 'edit', label: 'Can edit' },
  { level: 'comment', label: 'Can comment' },
  { level: 'read', label: 'Can view' },
]
/** Can comment is a document guest's level (RFC 0345 3g); a project or
 * session reader comments already. */
const levelsFor = (kind: 'session' | 'project' | 'document') =>
  kind === 'document' ? LEVELS : LEVELS.filter((l) => l.level !== 'comment')

type ShareTargetProps = {
  documentId: string
  kind: 'session' | 'project' | 'document'
  /** A document's project, whose catalog names its embeds (3d). */
  projectId?: string
}

export function ShareTarget(props: ShareTargetProps) {
  const account = useSharingAccount()
  const { sharing } = useTabRepoBoot()
  // RFC 0345 3h: a Mac's window has no account of its own; its daemon shares.
  const invite = desktopInviteFor()
  if ((!account || !sharing) && props.kind === 'session' && invite)
    return <DesktopShareTarget sessionId={props.documentId} invite={invite} />
  return <AccountShareTarget {...props} />
}

/** A session's Share on a Mac: invites go through this Mac's daemon, and who
 * can ask the agent is written on the session as anywhere else. */
function DesktopShareTarget({
  sessionId,
  invite,
}: {
  sessionId: string
  invite: DesktopInvite
}) {
  const [session, changeSession] = useDocument<{ askAgent?: unknown }>(
    sessionId as AnyDocumentId,
  )
  const worldId = (() => {
    try {
      return localStorage.getItem(WORLD_KEY)?.replace(/^automerge:/, '')
    } catch {
      return undefined
    }
  })()
  const [world] = useDocument<WorldDoc>(worldId as AnyDocumentId | undefined)
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLDivElement>(null)
  // Who can see it, from this Mac's daemon: on open, then while it stays open.
  const [rows, setRows] = useState<MemberRow[]>([])
  useEffect(() => {
    const members = desktopMembersFor()
    if (!open || !members) return
    let live = true
    const read = () =>
      void members(sessionId)
        .then((found) => {
          if (live) setRows(found as MemberRow[])
        })
        .catch(() => {})
    read()
    const timer = setInterval(read, 10_000)
    return () => {
      live = false
      clearInterval(timer)
    }
  }, [open, sessionId])
  return (
    <div ref={anchor} className="relative">
      <MemberStack
        rows={rows}
        open={open}
        kind="session"
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <ShareDropdown
          anchor={anchor.current}
          kind="session"
          onClose={() => setOpen(false)}
          invites={uniqueActiveInvites(world?.sharingInvites, sessionId)}
          send={async (email, level, agentReach) => {
            const { link } = await invite({
              sessionId,
              email,
              level: level === 'read' ? 'read' : 'edit',
              ...(agentReach ? { agentReach } : {}),
            })
            return { link, emailed: false }
          }}
          members={rows}
          canInvite
          askAgent={{
            onlyOwner: session?.askAgent === 'owner',
            set: (onlyOwner) =>
              changeSession((d) => {
                if (onlyOwner) d.askAgent = 'owner'
                else delete d.askAgent
              }),
          }}
        />
      )}
    </div>
  )
}

function AccountShareTarget({
  documentId: sessionId,
  kind,
  projectId,
}: ShareTargetProps) {
  const account = useSharingAccount()
  const { sharing } = useTabRepoBoot()
  const [session, changeSession] = useDocument<{
    askAgent?: unknown
    sharingGroupId?: unknown
    title?: unknown
    path?: unknown
    mime?: unknown
    content?: unknown
  }>(sessionId as AnyDocumentId)
  const [world] = useDocument<WorldDoc>(
    account?.opened.location.worldId as AnyDocumentId | undefined,
  )
  const plan = useDocumentPlan(
    kind === 'document' ? projectId : undefined,
    sessionId,
    session,
    world,
    sharing?.repo,
  )
  const membership = useTargetMembership(sessionId, kind)
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLDivElement>(null)
  if (!account || !sharing || !session) return null
  if (
    kind === 'project' &&
    String(world?.sharedDocumentsProjectId) === sessionId
  )
    return null
  const pointer = String(session.sharingGroupId ?? '')
  const owners = membership.ownerGroups ?? []
  // Metadata may discover a group, but only a signed ownership grant can
  // select it. Missing/forged metadata must not hide the membership list.
  const groupId = owners.includes(pointer)
    ? pointer
    : owners.length === 1
      ? owners[0]
      : undefined
  const invites = uniqueActiveInvites(world?.sharingInvites, sessionId)
  const canInvite = membership.level >= 2 && (kind === 'document' || !!groupId)
  // S12: an owner removes a person from a project or session. The confirm is
  // built from signed grants: whose agents leave, which stay and why.
  const self = Array.from(account.opened.person?.toBytes?.() ?? [], (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('')
  const memberRemoval = (row: MemberRow): Removal | undefined => {
    if (kind === 'document' || membership.level < 3 || !groupId) return
    if (row.kind !== 'person' || row.id === self || owners.includes(row.id))
      return
    // Our own pending invite keys are the only direct key grants a removal
    // may leave in place; any other refuses it (core removal.ts).
    const inviteKeys = Object.values(world?.sharingInvites ?? {})
      .filter(
        (invite) =>
          invite.status === 'pending' &&
          String(invite.target.documentId) === sessionId,
      )
      .map((invite) => String(invite.inviteKey))
    const target = { kind, documentId: sessionId, groupId, inviteKeys }
    const nameOf = (id: string) =>
      membership.rows.find((r) => r.id === id)?.name ??
      `person ${id.slice(0, 8)}`
    const agentName = (key: string) =>
      membership.rows.find((r) => r.id === key)?.name ??
      `agent ${key.slice(0, 8)}`
    return {
      where: `this ${kind}`,
      preview: async () => {
        const { previewMemberRemoval } = await import('@mod/core/sharing')
        const p = await previewMemberRemoval(sharing, target, row.id)
        return {
          lines: [
            ...p.agentsLeaving.map(
              (key) => `${agentName(key)} leaves with them.`,
            ),
            ...p.agentsStaying.map(
              (agent) =>
                `${agentName(agent.key)} stays: ${agent.grantedBy.map(nameOf).join(', ')} granted it too. Revoke it there to remove it everywhere.`,
            ),
            ...(p.invitees.length
              ? [
                  `${p.invitees.map((i) => nameOf(i.id)).join(', ')} stay unless also removed.`,
                ]
              : []),
          ],
          offers: p.invitees.map((i) => ({ id: i.id, name: nameOf(i.id) })),
          ...(p.blocked ? { blocked: p.blocked } : {}),
        }
      },
      remove: async (alsoRemove = []) => {
        const { removeMember } = await import('@mod/core/sharing')
        await removeMember(sharing, target, row.id, alsoRemove)
      },
    }
  }
  // S12: a page share removes the people its settle admitted, by signed id.
  const removalFor = (row: MemberRow): Removal | undefined => {
    if (kind !== 'document') return memberRemoval(row)
    if (!canInvite || row.kind !== 'person') return
    const shares = Object.entries(world?.sharingInvites ?? {}).filter(
      ([, invite]) =>
        invite.status === 'joined' &&
        invite.target.kind === 'document' &&
        invite.target.documentId === sessionId &&
        invite.target.membered.kind === 'group' &&
        (invite.joined ?? []).some((id) => String(id) === row.id),
    )
    if (!shares.length) return
    const kept = retainedDocumentIds(world?.sharingInvites, row.id, sessionId)
    const keeps = shares
      .flatMap(([, invite]) => invite.target.includes ?? [])
      .filter((include) => kept.has(include.documentId))
      .map((include) => include.title)
    return {
      ...(keeps.length
        ? {
            keeps: `They keep ${[...new Set(keeps)].join(', ')} through another share.`,
          }
        : {}),
      remove: async () => {
        const { removeDocumentGuest } = await import('@mod/core/sharing')
        for (const [, invite] of shares)
          await removeDocumentGuest(sharing, invite.target as never, [row.id])
        const handle = await sharing.repo.find<WorldDoc>(
          account.opened.location.worldId as AnyDocumentId,
        )
        handle.change((doc) => {
          for (const [id] of shares) {
            const entry = doc.sharingInvites?.[id]
            if (!entry) continue
            const left = (entry.joined ?? [])
              .map(String)
              .filter((person) => person !== row.id)
            entry.joined = left
            if (!left.length) entry.status = 'removed'
          }
        })
      },
    }
  }

  return (
    <div ref={anchor} className="relative">
      <MemberStack
        rows={membership.rows}
        open={open}
        kind={kind}
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <ShareDropdown
          anchor={anchor.current}
          kind={kind}
          onClose={() => setOpen(false)}
          invites={invites}
          plan={plan}
          send={async (email, level, agentReach) => {
            const planned = kind === 'document' ? plan?.(level) : undefined
            // Unknown embeds would leave the guest blank ones: wait instead.
            if (kind === 'document' && !planned)
              throw new Error('Still reading what this document embeds')
            if (planned && !planned.ok) throw new Error(planned.reason)
            // Sharing code loads only where sharing is on.
            const { sendInvite } = await import('./invites')
            const { inviteDocumentId } = await import('@mod/core/sharing')
            const handle = await sharing.repo.find(sessionId as AnyDocumentId)
            const membered =
              kind === 'document'
                ? {
                    kind: 'document' as const,
                    id: inviteDocumentId(handle.url),
                  }
                : { kind: 'group' as const, id: String(groupId) }
            const includes: InviteInclude[] = []
            for (const grant of planned?.grants ?? []) {
              const found = await sharing.repo.find(grant.id as AnyDocumentId)
              includes.push({
                documentId: grant.id,
                id: inviteDocumentId(found.url),
                level: grant.level,
                why: grant.why,
                title: grant.title,
                ...(grant.document ? { document: grant.document } : {}),
              })
            }
            return sendInvite({
              sharing,
              client: account.client,
              account: account.account,
              world: (await sharing.repo.find<WorldDoc>(
                account.opened.location.worldId as AnyDocumentId,
              )) as never,
              email,
              level,
              ...(agentReach ? { agentReach } : {}),
              ...(kind === 'document'
                ? { ownerPerson: account.opened.person }
                : {}),
              target: {
                kind,
                documentId: sessionId,
                membered,
                ...(kind === 'document'
                  ? {
                      document: {
                        path: String(session.path ?? 'Shared document.md'),
                        mime: String(session.mime ?? 'text/markdown'),
                      },
                      ...(includes.length ? { includes } : {}),
                    }
                  : {}),
                title: String(
                  (session as { title?: unknown }).title ??
                    session.path ??
                    kind,
                ),
              },
            })
          }}
          members={membership.rows}
          membershipError={membership.error}
          canInvite={canInvite}
          removalFor={removalFor}
          {...(kind === 'session' && membership.level >= 3
            ? {
                askAgent: {
                  onlyOwner: session.askAgent === 'owner',
                  // RFC 0345 A3: the owner's daemon reads this before it
                  // answers a member (session-agents watchMemberAsks).
                  set: (onlyOwner: boolean) =>
                    changeSession((d) => {
                      if (onlyOwner) d.askAgent = 'owner'
                      else delete d.askAgent
                    }),
                },
              }
            : {})}
        />
      )}
    </div>
  )
}

type PlanOf = (level: InviteLevel) => EmbedPlan | null

/** What a document invite hands over with the page, for the level chosen:
 * its embeds and what draws them (3d). Null until the catalog has loaded,
 * and absent for a project or session. */
function useDocumentPlan(
  projectId: string | undefined,
  documentId: string,
  page:
    | { title?: unknown; path?: unknown; mime?: unknown; content?: unknown }
    | undefined,
  world: WorldDoc | undefined,
  repo:
    | {
        find: (id: AnyDocumentId) => Promise<{
          doc(): unknown
          on?(event: 'change', fn: () => void): unknown
          off?(event: 'change', fn: () => void): unknown
        }>
      }
    | undefined,
): PlanOf | undefined {
  const [project] = useDocument<ProjectDoc>(
    projectId as AnyDocumentId | undefined,
  )
  // Embedded pages' own markdown, read as the plan asks for it and followed
  // while the dialog is up, so their embeds are named as they are now. Null is
  // a page that could not be read: the share refuses and says which.
  const [contents, setContents] = useState<Record<string, string | null>>({})
  const wanted = useRef(new Set<string>())
  const asked = useRef(new Set<string>())
  const stops = useRef<Array<() => void>>([])
  useEffect(
    () => () => {
      for (const stop of stops.current) stop()
    },
    [],
  )
  useEffect(() => {
    if (!repo) return
    for (const id of wanted.current) {
      if (asked.current.has(id)) continue
      asked.current.add(id)
      void repo
        .find(id as AnyDocumentId)
        .then((handle) => {
          const read = () => {
            const doc = handle.doc() as { content?: unknown } | undefined
            setContents((had) => ({
              ...had,
              [id]: String(doc?.content ?? ''),
            }))
          }
          read()
          handle.on?.('change', read)
          stops.current.push(() => handle.off?.('change', read))
        })
        .catch(() => setContents((had) => ({ ...had, [id]: null })))
    }
  })
  const catalog = useMemo(
    () => (project && world ? catalogOf(project, world) : null),
    [project, world],
  )
  const plan = useCallback<PlanOf>(
    (level) => {
      if (!catalog || !page || level === 'admin') return null
      const subject = (id: string) => embedSubject(catalog, id)
      return planDocumentShare({
        document: subject(documentId) ?? {
          id: documentId,
          title: String(page.title ?? page.path ?? 'Document'),
          mime: String(page.mime ?? 'text/markdown'),
        },
        content: String(page.content ?? ''),
        level,
        resolve: (href) => {
          if (/^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i.test(href)) return null
          const byPath = catalog.documents.find((d) => d.path === href)
          return subject(href) ?? (byPath ? subject(byPath.id) : null)
        },
        apps: catalog.apps,
        pathOf: (id) => catalog.documents.find((d) => d.id === id)?.path,
        contentOf: (id) => {
          if (!(id in contents)) wanted.current.add(id)
          return contents[id]
        },
      })
    },
    [catalog, page, documentId, contents],
  )
  return projectId ? plan : undefined
}

/** A catalog id as the plan reads it, the way a link to it would resolve. */
function embedSubject(
  catalog: ProjectCatalog,
  id: string,
): EmbedSubject | null {
  const subject = resolveSubject(catalog, id, {
    webFileFromId: () => null,
    canEmbedWeb: false,
  })
  if (!subject) return null
  const entry = catalog.documents.find((d) => d.id === subject.id)
  // A file off a branch resolves too, but is not a document anyone can grant.
  if (!entry && !catalog.apps.some((a) => a.id === subject.id)) return null
  return {
    id: subject.id,
    title: subject.title,
    path: subject.path,
    mime: entry?.mime ?? subject.mime,
    ...(entry?.dataType ? { dataType: entry.dataType } : {}),
    ...(subject.appId ? { appId: subject.appId } : {}),
    ...(subject.missingApp ? { missingApp: subject.missingApp } : {}),
  }
}

export function ShareDropdown({
  anchor,
  kind,
  invites,
  plan,
  send,
  onClose,
  members,
  membershipError,
  canInvite,
  removalFor,
  askAgent,
}: {
  anchor: HTMLDivElement | null
  kind: 'session' | 'project' | 'document'
  invites: Array<[string, NonNullable<WorldDoc['sharingInvites']>[string]]>
  /** A document's: what goes with it at each level. */
  plan?: PlanOf
  send: (
    email: string,
    level: InviteLevel,
    agentReach?: 'session' | 'full',
  ) => Promise<{ link: string; emailed: boolean }>
  onClose: () => void
  members: MemberRow[]
  membershipError?: string
  canInvite: boolean
  removalFor?: (row: MemberRow) => Removal | undefined
  /** A session owner's: who may ask the session's agent (RFC 0345 A3). */
  askAgent?: { onlyOwner: boolean; set: (onlyOwner: boolean) => void }
}) {
  const [email, setEmail] = useState('')
  const [level, setLevel] = useState<InviteLevel>('edit')
  // RFC 0345 A2: what their requests to the session's agent reach.
  const [agentReach, setAgentReach] = useState<'session' | 'full'>('session')
  const planned = plan?.(level)
  const refused = planned && !planned.ok ? planned.reason : undefined
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState<{ link: string; emailed: boolean }>()
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const trigger = anchor?.querySelector('button')
    const focus = ref.current?.querySelector('input') ?? ref.current
    focus?.focus()
    return () => trigger?.focus()
  }, [anchor])
  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(e.target as Node) &&
        !anchor?.contains(e.target as Node)
      )
        onClose()
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      onClose()
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc, true)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', esc, true)
    }
  }, [onClose, anchor])

  const bounds = anchor?.getBoundingClientRect()
  return createPortal(
    <div
      ref={ref}
      role="dialog"
      tabIndex={-1}
      aria-label={`Share ${kind}`}
      style={{
        top: Math.max(
          8,
          Math.min(bounds?.bottom ?? 0, window.innerHeight - 280) + 6,
        ),
        left: Math.max(
          8,
          Math.min((bounds?.right ?? 328) - 320, window.innerWidth - 328),
        ),
      }}
      className="fixed z-50 max-h-[calc(100vh-16px)] w-80 max-w-[calc(100vw-16px)] space-y-3 overflow-y-auto rounded-lg border border-border bg-background p-3 text-sm shadow-lg"
    >
      {canInvite && (
        <form
          className="flex gap-1.5"
          onSubmit={async (e) => {
            e.preventDefault()
            setBusy(true)
            setError('')
            setSent(undefined)
            try {
              setSent(
                await send(
                  email,
                  level,
                  kind === 'session' ? agentReach : undefined,
                ),
              )
              setEmail('')
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Invite failed')
            } finally {
              setBusy(false)
            }
          }}
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Add people by email"
            aria-label="Email to invite"
            className="min-w-0 flex-1 rounded border border-border bg-transparent px-2 py-1 text-sm"
          />
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value as InviteLevel)}
            aria-label="Access"
            className="rounded border border-border bg-transparent px-1 text-xs"
          >
            {levelsFor(kind).map((l) => (
              <option key={l.level} value={l.level}>
                {l.label}
              </option>
            ))}
          </select>
          {kind === 'session' && (
            <select
              value={agentReach}
              onChange={(e) =>
                setAgentReach(e.target.value as 'session' | 'full')
              }
              aria-label="What their requests to the agent reach"
              title="Session only: the agent answers them with this session alone. Full access: with everything the agent reaches, the attached projects too."
              className="rounded border border-border bg-transparent px-1 text-xs"
            >
              <option value="session">Session only</option>
              <option value="full">Full access</option>
            </select>
          )}
          <button
            type="submit"
            disabled={busy || !!refused}
            className="rounded border border-border px-2 text-xs hover:bg-muted"
          >
            {busy ? '…' : 'Invite'}
          </button>
        </form>
      )}
      {canInvite && planned?.ok && planned.grants.length > 0 && (
        <div className="space-y-1 text-xs">
          <p className="text-muted-foreground">They will also get:</p>
          {planned.grants.map((grant) => (
            <Row
              key={grant.id}
              name={grant.title}
              why={
                grant.why === 'embed'
                  ? `embedded, ${LEVELS.find((l) => l.level === grant.level)?.label.toLowerCase()}`
                  : grant.why === 'app'
                    ? 'draws it, can view'
                    : 'its code, can view'
              }
            />
          ))}
        </div>
      )}
      {canInvite && refused && (
        <p role="alert" className="text-xs">
          Cannot share yet: {refused}
        </p>
      )}
      {error && (
        <p role="alert" className="text-xs">
          {error}
        </p>
      )}
      {sent && (
        <div className="space-y-1 text-xs">
          <p className="text-muted-foreground">
            {sent.emailed
              ? 'Invite sent. You can also copy the link.'
              : 'Copy this private invite link and send it to them:'}
          </p>
          <div className="flex gap-1.5">
            <input
              readOnly
              value={sent.link}
              aria-label="Invite link"
              className="min-w-0 flex-1 rounded border border-border bg-transparent px-2 py-1 font-mono text-[11px]"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              className="rounded border border-border px-2 hover:bg-muted"
              onClick={() => void navigator.clipboard?.writeText(sent.link)}
            >
              Copy
            </button>
          </div>
        </div>
      )}
      {askAgent && (
        <fieldset className="space-y-1 text-xs">
          <legend className="text-muted-foreground">
            Who can ask the agent
          </legend>
          {(
            [
              [false, 'Anyone here'],
              [true, 'Only I can'],
            ] as const
          ).map(([only, label]) => (
            <label key={label} className="flex items-center gap-2">
              <input
                type="radio"
                name="ask-agent"
                checked={askAgent.onlyOwner === only}
                onChange={() => askAgent.set(only)}
              />
              {label}
            </label>
          ))}
        </fieldset>
      )}
      <MemberList
        rows={members}
        error={membershipError}
        {...(removalFor ? { removalFor } : {})}
      />
      {canInvite && (
        <div className="space-y-1">
          <p className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
            Invitations
          </p>
          {invites
            .filter(([, invite]) => invite.status !== 'joined')
            .map(([id, invite]) => (
              <Row
                key={id}
                name={invite.email}
                why={
                  invite.status === 'joined'
                    ? invite.level === 'read'
                      ? 'can view'
                      : 'can edit'
                    : invite.status === 'expired'
                      ? 'invite expired'
                      : invite.status === 'refused'
                        ? `not shared: ${String(invite.refused?.[0] ?? '')}`
                        : 'invited'
                }
                link={
                  invite.status === 'pending' && invite.link
                    ? String(invite.link)
                    : undefined
                }
              />
            ))}
        </div>
      )}
    </div>,
    document.body,
  )
}

function Row({
  name,
  why,
  link,
}: {
  name: string
  why: string
  /** A pending invite's private link, to send again. */
  link?: string
}) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="truncate">{name}</span>
      <span className="flex shrink-0 items-center gap-2 text-muted-foreground text-xs">
        {link && (
          <button
            type="button"
            className="rounded px-1 hover:bg-muted hover:text-foreground"
            onClick={() => {
              void navigator.clipboard?.writeText(link).then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
              })
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
        )}
        {why}
      </span>
    </div>
  )
}
